import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaticPage from "../../model/StaticPage";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import ButtonType from "../common/button/ButtonType";
import FormRichTextEditor from "../common/FormRichTextEditor";
import Loading from "../common/Loading";
import { useAuth } from "../../contexts/AuthContext";

interface IStaticPageDetailsProps {
  slug?: string;
}

function StaticPageDetails({ slug = "" }: IStaticPageDetailsProps) {
  // read the ID from the url
  const { id } = useParams();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<StaticPage>();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // authentication manager
  const authManager = useAuth();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);

    const getAllData = async () => {
      try {
        let pageInfo: StaticPage | undefined = undefined;
        if (id) {
          pageInfo = await apiClient?.get<StaticPage>({
            url: APIEndPoints.STATIC_PAGES.ONE(parseInt(id)),
          });
        } else if (slug != "") {
          pageInfo = await apiClient?.get<StaticPage>({
            url: APIEndPoints.STATIC_PAGES.SEARCHED(slug),
          });
        }
        setData(pageInfo);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="p-8 px-28">
      {authManager.isAdmin() && (
        <div className="flex justify-between">
          <Button
            label="Back to List"
            purpose={ButtonAction.BACK}
            type={ButtonType.ICON_TEXT}
            onClick={() =>
              navigate(
                SETTINGS.go_back({
                  section: AppSections.STATIC_PAGES,
                }),
              )
            }
          />
          <Button
            label="Edit content"
            purpose={ButtonAction.EDIT}
            type={ButtonType.ICON_TEXT}
            onClick={() =>
              navigate(
                SETTINGS.get_path({
                  section: AppSections.STATIC_PAGES,
                  id: data?.id,
                  subsection: AppSubSections.EDIT,
                }),
              )
            }
          />
        </div>
      )}
      <h1>{data?.title}</h1>
      <div className="my-8">
        <FormRichTextEditor
          id="license-view"
          value={data?.content ?? ""}
          required={false}
          onChange={() => {}} // ← no-op when readOnly
          readOnly={true}
          modules={{ toolbar: false }}
          className="quill-viewer h-full"
        />
      </div>
      {authManager.isAdmin() && (
        <div className="flex justify-between">
          <Button
            label="Back to List"
            purpose={ButtonAction.BACK}
            type={ButtonType.ICON_TEXT}
            onClick={() =>
              navigate(
                SETTINGS.go_back({
                  section: AppSections.STATIC_PAGES,
                }),
              )
            }
          />
          <Button
            label="Edit content"
            purpose={ButtonAction.EDIT}
            type={ButtonType.ICON_TEXT}
            onClick={() =>
              navigate(
                SETTINGS.get_path({
                  section: AppSections.STATIC_PAGES,
                  id: data?.id,
                  subsection: AppSubSections.EDIT,
                }),
              )
            }
          />
        </div>
      )}
    </div>
  );
}

export default StaticPageDetails;
